import 'regenerator-runtime/runtime';
import { NOT_FOUND, redirect } from 'redux-first-router';
import * as Sentry from '@sentry/browser';
import { checkProceed } from './actions/checkout';
import { deepClone } from './utils/object';
import { getCookie, setCookie } from './utils/dom';
import { translateObjectDefaultData, translate } from './utils/locale';
import { rootUrl, urlify } from './utils/string';
import { fetchData, fetchCheckout } from './utils/async';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState) => {
	const { properties, navigation } = getState().environment;

	if (navigation.open) dispatch({ type: 'OPEN_MENU' });
	if (properties.openCart === 1) {
		dispatch({ type: 'OPEN_CART' });
		setTimeout(() => {
			dispatch({ type: 'OPEN_CART' });
		}, 300);
	}

	if (typeof window !== 'undefined') {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'PageView',
			values: {
				path: getState().location.pathname,
			},
		});
	}
};

const clientInit = async (dispatch, getState) => {
	clientUpdate(dispatch, getState);

	const { setup, campaign = {}, postParameters = {}, getParameters = {} } = getState().environment;
	const { selection = {}, destorySelection = false } = getState().checkout;
	const receipt = getState().location.type === 'RECEIPT';
	let selectionData = {};
	let token = null;

	const savedConsent = getCookie('cookies', true);
	if (savedConsent && savedConsent !== 1) {
		const consent = {};
		consent.functionality_storage = savedConsent.indexOf('functionality') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_storage = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_user_data = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_personalization = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.analytics_storage = savedConsent.indexOf('analytics') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.security_storage = 'granted';
		consent.personalization_storage = 'granted';

		dispatch({ type: 'COOKIE_CONSENT' });
		if (typeof window.gtag === 'function') window.gtag('consent', 'update', consent);
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'ConsentUpdated',
			values: {},
		});
	}

	if (getState().location.type === 'RECEIPT' || getState().location.type === 'RECEIPT_DEBUG') {
		dispatch({ type: 'LIST_ITEM_CACHE' });
	}

	// Receipt component was not unmounted page was reloaded.
	if (destorySelection || (typeof document !== 'undefined' && getCookie('receiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION' });
		return dispatch({ type: 'START' });
	}

	if (!token && typeof localStorage !== 'undefined') {
		// transition to cookie based selection.
		token = localStorage.getItem('selection');
		if (token) {
			localStorage.removeItem('selection');
			setCookie('selection', token, 365);
		}
	}
	if (!token && selection.token) token = selection.token;
	if (!token && typeof document !== 'undefined') token = getCookie('selection');

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) dispatch({ type: 'INIT_SELECTION_CACHE', initCache: selectionCache });
	}

	if (Object.keys(campaign).length > 0 && campaign.site) {
		selectionData = await fetchCheckout('/campaign-site', token, { uri: campaign.site }, 'PUT');
	}

	if (!receipt) {
		if (!getState()?.listing?.magazine?.content?.[1]) {
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			fetchData('listing/magazine/1/date/-1', body).then((data) => (
				dispatch({ type: 'LISTING', data, listing: 'magazine', currentPage: 1, initial: 1 })
			)).catch((err) => {
				console.log('DEBUG: error retrieving story listing', err);
				Sentry.captureException(err);
			});
		}
		if (!getState()?.listing?.brands) {
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			fetchData('listing/brands', body).then((data) => (
				dispatch({ type: 'LISTING', data, listing: 'brands', currentPage: -1 })
			)).catch((err) => {
				console.log('DEBUG: error retrieving style listing', err);
				Sentry.captureException(err);
			});
		}

		if (token) {
			// All other pages except for receipt
			selectionData = fetchCheckout('/selection', token, [], 'GET');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}
		}
	} else if (receipt) {
		// Get post parameters from store.
		// Receipt page
		if (token) {
			try {
				const paymentMethodFields = { ...getParameters, ...postParameters };
				selectionData = await fetchCheckout('/payment-result', token, { paymentMethodFields }, 'POST');
			} catch (err) {
				return dispatch({ type: 'SELECTION_FAILED' });
			}
		} else {
			// Token has disappeared.
			// Not much to do about it and not neccessarily wrong.
			// User may have refreshed receipt page or tried to go back but was redirected to the receipt
			return dispatch({ type: 'START' });
		}

		if (selectionData.token && selectionData.order && !selectionData.errors) {
			// Purchase succeeded
			dispatch({ type: 'RECEIPT_DATA', data: selectionData });
		} else {
			// Selection errored out.
			// Need to get the original selection so the customer can try again.
			selectionData = fetchCheckout('/selection', token, [], 'GET');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}

			return dispatch({ type: 'SELECTION_FAILED' });
		}
	}

	const paymentInfo = JSON.parse(localStorage.getItem('paymentInfo'));
	if (paymentInfo) {
		dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo });
		if (getState().location.type === 'SELECTION' && selectionData?.selection?.items && selectionData.selection.items.length > 0) dispatch(checkProceed());
	}

	// If selection is set to another country than default change country data to that of selection.
	// Unless we are coercing the user to another country then we change in Centra.
	if (selectionData.location && selectionData.location.country !== setup.country) {
		if (getParameters.country || getParameters.forceCountry) {
			selectionData = await fetchCheckout(`/countries/${setup.country}`, token, [], 'PUT');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}
		} else {
			const countryData = setup.countries[selectionData.location.country];

			dispatch({ type: 'UPDATE_COUNTRY', country: countryData.country, market: countryData.market, pricelist: countryData.pricelist });
		}
	}

	// Ensure that we have the product cache for the current market and pricelist.
	// We load this in asyncronously because it's needed for search and will be needed for other pages if not already included.
	await retrieveProductCache(dispatch, getState);

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) {
			const productCache = getState().productCache[setup.market][setup.pricelist];
			const updatedProducts = Object.keys(selectionCache);
			const updatedSelectionCache = updatedProducts.reduce((acc, productKey) => ({ ...acc, [productKey]: deepClone(productCache.products[productKey]) }), {});
			dispatch({ type: 'UPDATE_SELECTION_CACHE', productCache: updatedSelectionCache, product: updatedProducts, selectionCache });
		}
	}

	let favourites = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('favourites')) : false;
	if (favourites) {
		const productCache = getState().productCache[setup.market][setup.pricelist];
		favourites = favourites.filter((favourite) => productCache.products[favourite] !== undefined);
		dispatch({ type: 'UPDATE_FAVOURITES', favourites });
	}

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const retrieveProductCache = async (dispatch, getState) => {
	const { setup } = getState().environment;
	const { previewCode, country, market, pricelist } = setup;
	let productCache;

	if (getState().productCache[market] && getState().productCache[market][pricelist]) return getState().productCache[market][pricelist];

	try {
		const body = previewCode ? { previewCode } : {};
		productCache = await fetchData(`filter-cache/${market}/${pricelist}/`, body);
	} catch (err) {
		console.log('DEBUG: filter cache not loaded');
	}

	dispatch({ type: 'PRODUCT_CACHE', market, pricelist, setup, productCache });
	return dispatch({ type: 'UPDATE_COUNTRY', market, pricelist, country });
};

const languagePrefix = '/:language?';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => console.log('DEBUG: error initiating client site data', err)),
	},
	RETRIEVE_PRODUCT_CACHE: {
		thunk: async (dispatch, getState) => (
			retrieveProductCache(dispatch, getState)
		),
	},
	STATIC: {
		path: '/static/:noop?',
	},
	REFERRAL: {
		path: '/ref',
		thunk: async (dispatch, getState, bag) => {
			const { location, environment } = getState();
			const { getParameters = {}, setup } = environment;
			const { hash = '' } = bag.action.payload;
			let path = '';
			let search = '';
			const goTo = Object.keys(getParameters).reduce((acc, param) => (param.toLowerCase() === 'goto' ? getParameters[param] : acc), '');
			if (goTo) path = decodeURIComponent(goTo).replace(/^\/+/, '');
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			if (path === '/' || path === '') return dispatch(redirect({ type: 'START', payload: { language: setup.language, path: '', search, hash } }));
			return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language: setup.language, path, search, hash } }));
		},
	},
	START: {
		path: useLanguage(),
		thunk: async (dispatch, getState, bag) => {
			const { languages } = getState().environment.setup;

			// best guess that it's start page otherwise cached...
			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const startPage = !getState().pageData || !getState().pageData.start ? fetchData('', body) : getState().pageData.start;
			let { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			dispatch({ type: 'PAGE_PATHS', pageType: 'start', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) });

			if (startPageData?.content?.pageHasProducts) {
				await retrieveProductCache(dispatch, getState);
			}
		},
	},
	SEARCH: {
		path: useLanguage('/search'),
		thunk: async (dispatch, getState, bag) => {
			const { languages } = getState().environment.setup;
			let { hash = '' } = bag.action.payload;

			clientUpdate(dispatch, getState);

			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			dispatch({ type: 'PAGE_PATHS', pageType: 'search', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'search' }), {}) });

			if (getState().location.query && Object.keys(getState().location.query).length > 0 && Object.keys(getState().location.query)[0] !== '') {
				dispatch({ type: 'SEARCH', text: Object.keys(getState().location.query)[0] });
			}

			await retrieveProductCache(dispatch, getState);
		},
	},
	SELECTION: {
		path: useLanguage('/selection'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			const productCache = retrieveProductCache(dispatch, getState);
			try {
				await productCache;
			} catch (err) {
				console.log('DEBUG: product cache not retrieved', err);
			}

			dispatch({ type: 'PAGE_PATHS', pageType: 'selection', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) });
		},
	},
	SELECTION_FAILED: {
		path: useLanguage('/selection/failed'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			let failedPageData;

			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const failedPage = !getState().pageData || !getState().pageData['selection/failed'] ? fetchData('selection/failed', body) : getState().pageData['selection/failed'];
			clientUpdate(dispatch, getState);

			try {
				failedPageData = await failedPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: failedPageData, page: 'selection/failed' });
			dispatch({ type: 'PAGE_PATHS', pageType: 'selectionFailed', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/failed' }), {}) });
		},
	},
	RECEIPT: {
		path: useLanguage('/selection/receipt'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			dispatch({ type: 'PAGE_PATHS', pageType: 'receipt', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt' }), {}) });
		},
	},
	FAVOURITES: {
		path: useLanguage('/favourites'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			dispatch({ type: 'PAGE_PATHS', pageType: 'favourites', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'favourites' }), {}) });

			retrieveProductCache(dispatch, getState);
		},
	},
	RECEIPT_DEBUG: {
		path: useLanguage('/selection/receipt/debug'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			dispatch({ type: 'PAGE_PATHS', pageType: 'receiptDebug', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt/debug' }), {}) });
		},
	},
	SELECTION_RECOVER: {
		path: useLanguage('/selection/recover/:newToken*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { payload, search } = location;
			const { newToken = '' } = payload;
			let { language = false } = payload;
			if (!language) language = environment.setup.language;

			if (!newToken) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const selectionData = await fetchCheckout(`/selection/${newToken}`, false, {}, 'PUT');
				if (selectionData.token) {
					dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
					return dispatch(redirect({ type: 'SELECTION', payload: { language, ...(search ? { search: `?${search}` } : {}) } }));
				}
				return dispatch(redirect({ type: 'START', payload: { language, ...(search ? { search: `?${search}` } : {}) } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language, ...(search ? { search: `?${search}` } : {}) } }));
			}
		},
	},
	SELECTION_PAYMENT_LINK: {
		path: useLanguage('/selection/:paymentLink*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { selection = {} } = getState().checkout;
			const { paymentLink = '' } = location.payload;
			let { language = false } = location.payload;
			if (!language) language = environment.setup.language;

			let token = null;
			if (!token && selection.token) token = selection.token;
			if (!token && typeof document !== 'undefined') token = getCookie('selection');

			if (!paymentLink) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const paymentLinkResult = await fetchData('payment-links', { paymentLink });
				if (paymentLinkResult?.success && paymentLinkResult?.data) {
					const selectionData = await fetchCheckout(`/selection/${paymentLinkResult.data}`, token, {}, 'PUT');
					if (selectionData.token) {
						dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
						const payload = language === 'en' ? {} : { language };
						return dispatch(redirect({ type: 'SELECTION', payload }));
					}
				}
				return dispatch(redirect({ type: 'START', payload: { language } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}
		},
	},
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path+'),
		thunk: async (dispatch, getState, bag) => {
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment } = getState();
			const { brands, magazine, setup } = environment;
			const productListPages = ['category', 'product'];
			let { hash = '' } = bag.action.payload;
			let language = languagePrefix !== '' ? location.payload.language : false;
			// Clean any encoded URI characters
			let path = location.payload.path.replace(/^\/+/, '').split('/').map((p) => decodeURI(p)).join('/');
			const pathWithoutQuery = rootUrl(path, '?');
			const search = location?.payload?.search || '';

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			/*
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			*/
			// "language" is probably not a language but a path.
			if (!language && language.length > 2) {
				path = `${language}/${path}`;
				language = false;
			}

			// Get content for site.
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			const page = !getState()?.pageData?.[pathWithoutQuery] ? fetchData(pathWithoutQuery, body) : getState().pageData[pathWithoutQuery];
			let listing = null;
			clientUpdate(dispatch, getState);
			let pageDataReturn;
			let currentPage = 1;
			let listingType;
			let listingDataReturn;

			const productCache = retrieveProductCache(dispatch, getState);

			const brandBase = translateObjectDefaultData(setup, brands?.posts?.[0]?.meta);
			const magazineBase = translateObjectDefaultData(setup, magazine?.posts?.[0]?.meta);
			if (magazineBase?.uri === pathWithoutQuery || magazine?.posts?.[0]?.meta?.[setup.language]?.uri === pathWithoutQuery) {
				if (/page=\d+/.test(search)) [, currentPage] = search.match(/page=(\d+)/);
				listingType = 'magazine';
				if (!getState()?.listing?.magazine || getState().listing.magazine.pagesRequested.indexOf(currentPage) === -1) {
					listing = fetchData(`listing/magazine/${currentPage}/date/-1`, body);
				} else {
					listing = getState().listing.magazine;
				}
			}
			if (brandBase?.uri === pathWithoutQuery || brands?.posts?.[0]?.meta?.[setup.language]?.uri === pathWithoutQuery) {
				currentPage = -1;
				listingType = 'brands';
				listing = getState()?.listing?.brands || fetchData('listing/brands', body);
			}

			try {
				[pageDataReturn, listingDataReturn] = await Promise.all([page, listing]);
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			if (pageDataReturn.paths) {
				if (Array.isArray(pageDataReturn.content) && pageDataReturn.content[0] && pageDataReturn.content[0].restrict && pageDataReturn.content[0].restrict.indexOf(language) !== -1) {
					dispatch({ type: 'PAGE_PATHS', pageType: pageDataReturn.pageType, paths: pageDataReturn.paths, restrict: pageDataReturn.content[0].restrict });
					dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: '404' });
					return dispatch({ type: NOT_FOUND });
				}

				dispatch({ type: 'PAGE_PATHS', pageType: pageDataReturn.pageType, paths: pageDataReturn.paths });

				if (pathWithoutQuery !== pageDataReturn.paths[language] && !pageDataReturn?.content?.is404 && pageDataReturn.paths[language]) {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			if (productListPages.indexOf(pageDataReturn.pageType) !== -1 || pageDataReturn?.content?.pageHasProducts) {
				try {
					await productCache;
				} catch (err) {
					console.log('DEBUG: product cache not retrieved', err);
				}
			}

			if (listingDataReturn) dispatch({ type: 'LISTING', data: listingDataReturn, listing: listingType, currentPage: parseInt(currentPage, 10) });

			const hashFilter = [];
			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });
			if (hash) {
				hash.slice(1).split('&').forEach((filter) => {
					const filterParts = filter.split('=');
					if (filterParts.length === 1) {
						hashFilter.push({ [filterParts[0]]: true });
					} else {
						hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
					}
				});
			}

			// History changed forward/back navigation reset filter
			const resetFilterHistoryChange = (location.kind === 'pop' && location.prev && location.prev.payload.path.split('/')[0] !== path.split('/')[0]);
			if (productListPages.indexOf(pageDataReturn.pageType) !== -1 && (location.kind !== 'pop' || resetFilterHistoryChange)) {
				const campaign = {
					newProducts: translate(setup, 'filterNewUri'),
					productsOnSale: translate(setup, 'filterSaleUri'),
				};
				const filterPath = pageDataReturn.pageType === 'product' ? pathWithoutQuery.slice(0, pathWithoutQuery.lastIndexOf('/')) : pathWithoutQuery;
				const pathSegments = filterPath.split('/');

				if (pageDataReturn.baseType === 'brands') {
					// Brand page
					const filterArray = [{ brands: urlify(pathSegments[1]) }];
					let campaignURI = false;
					let category = false;
					if (pathSegments.indexOf(campaign.productsOnSale) === pathSegments.length - 1 || pathSegments.indexOf(campaign.newProducts) === pathSegments.length - 1) {
						campaignURI = pathSegments.indexOf(campaign.productsOnSale) === pathSegments.length - 1 ? 'productsOnSale' : 'newProducts';
						category = pathSegments.slice(2, -1).join('/');
					} else {
						category = pathSegments.slice(2).join('/');
					}

					if (category) filterArray.push({ categories: category });
					if (campaignURI) filterArray.push({ [campaignURI]: true });
					filterArray.push({ sort: 'added-descending' });

					dispatch({ type: 'RESET_FILTER' });
					dispatch({ type: 'FILTER', filterArray: [...filterArray, ...hashFilter], force: true });
				} else if (filterPath.indexOf(campaign.productsOnSale) !== -1 || filterPath.indexOf(campaign.newProducts) !== -1) {
					// Campaign page + possible category
					const filterArray = [];

					if (filterPath.indexOf(campaign[pageDataReturn.campaignType]) === 0) {
						const category = pathSegments.slice(1).join('/');
						filterArray.push({ [pageDataReturn.campaignType]: true });
						if (category) filterArray.push({ categories: category });
					} else {
						const category = pathSegments.slice(0, -1).join('/');
						if (category) filterArray.push({ categories: category });
						filterArray.push({ [pageDataReturn.campaignType]: true });
					}

					filterArray.push({ sort: 'added-descending' });

					dispatch({ type: 'RESET_FILTER' });
					dispatch({ type: 'FILTER', filterArray: [...filterArray, ...hashFilter], force: true });
				} else {
					// Category
					dispatch({ type: 'RESET_FILTER' });
					dispatch({ type: 'FILTER', filterArray: [{ categories: filterPath }, ...hashFilter], force: true });
				}
			}

			if (pageDataReturn?.content?.is404) {
				dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: '404' });
				return dispatch({ type: NOT_FOUND });
			}

			return dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: pathWithoutQuery });
		},
	},
};
